import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useUser } from "../users/providers/UserProvider";
import ROUTES from "./routesModel";

/**
 * Admin-only route guard.
 *
 * All nine admin routes were wrapped in RequireAuth, which only checks that
 * SOMEONE is signed in — it has no admin prop and no isAdmin check. So any
 * authenticated user could open /admin/reports, /admin/moderation,
 * /admin/deletion-requests and the rest and see the admin UI shell.
 *
 * No data leaked: every admin-pathed route on the server carries verifyAdmin,
 * and that reads isAdmin from the database rather than the token. That is
 * exactly why this matters — the client guard was contributing nothing, so the
 * server check was the ONLY line of defence, and one route mounted without
 * verifyAdmin would have been a full exposure with nothing client-side to catch
 * it.
 *
 * This is defence in depth and a UX fix. It does NOT replace verifyAdmin: every
 * admin endpoint must keep its own server-side check, because anything shipped
 * to a browser can be bypassed.
 */
const RequireAdmin = ({ children }) => {
  const { user, authReady } = useUser();
  const location = useLocation();

  // Auth state has not hydrated yet — rendering nothing beats treating "unknown"
  // as "not an admin" and bouncing someone who is one.
  if (!authReady) return null;

  if (!user) {
    return <Navigate to={ROUTES.LOGIN} state={{ from: location }} replace />;
  }

  // Home, not login: they ARE authenticated, just not an admin. Sending them to
  // a sign-in form would suggest the fix is signing in again.
  if (!user.isAdmin) return <Navigate to={ROUTES.ROOT} replace />;

  return children;
};

export default RequireAdmin;
